import React, { useEffect, useState } from "react";
import {
  ExternalLink,
  FileText,
  FileX,
  PackageOpen,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Drawer } from "../../components/ui/Drawer";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { Table, THead, TBody, TR, TH, TD } from "../../components/ui/Table";
import { Skeleton } from "../../components/ui/Skeleton";
import { cn } from "../../utils/cn";
import { safeFileUrl } from "../../utils/safeUrl";
import { apiBodyMessage } from "../../utils/apiErrorMessage";
import { formatDate, formatTime, formatDateTime } from "../../utils/formatters";
import { formatRut } from "../../utils/formatter";
import { getHoldingCompanies, getHoldingPickups } from "../../api/holding";
import DateRangeFilter, { rangeFromPreset } from "../../components/dateRangeFilter/DateRangeFilter";
import { useUrlState } from "../../hooks/useUrlState";

const isoStartOfDay = (s) => `${s}T00:00:00.000Z`;
const isoEndOfDay = (s) => `${s}T23:59:59.999Z`;

// Deriva {desde, hasta} (YMD) desde un preset canónico de DateRangeFilter.
// "custom" (sin from/to persistidos) cae al default histórico de 7 días.
function deriveRange(preset) {
  const key = preset === "custom" ? "7d" : preset;
  const [s, e] = rangeFromPreset(key);
  return { desde: s.format("YYYY-MM-DD"), hasta: e.format("YYYY-MM-DD") };
}

function getPickupAddress(p) {
  return [p?.shippingAddresses?.address, p?.shippingAddresses?.municipality]
    .filter(Boolean)
    .join(", ");
}

export default function HoldingPickups() {
  const { t } = useTranslation();

  // Filtros persistidos en la URL. Default "7d" ⇒ sin params el rango es
  // idéntico al histórico (últimos 7 días); company vacío ⇒ todas las empresas.
  const [companyId, setCompanyId] = useUrlState("company", "");
  const [preset, setPreset] = useUrlState("preset", "7d");
  const [range, setRange] = useState(() => deriveRange(preset));

  const [companies, setCompanies] = useState([]);

  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let active = true;
    getHoldingCompanies({ page: 1, limit: 100 }).then((res) => {
      if (!active || res?.error) return;
      setCompanies(res.docs || []);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getHoldingPickups({
      startDate: isoStartOfDay(range.desde),
      endDate: isoEndOfDay(range.hasta),
      companyId: companyId || undefined,
    })
      .then((res) => {
        if (!active) return;
        if (res?.error) {
          setError(res.error);
          setPickups([]);
          return;
        }
        setPickups(Array.isArray(res?.data) ? res.data : []);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [range, companyId]);

  // Cambio de rango desde el DateRangeFilter (preset rápido o personalizado).
  // Recibe fechas "YYYY-MM-DD"; actualiza el scope (re-consulta vía effect).
  const handleRangeChange = (startYMD, endYMD, p) => {
    setPreset(p);
    setRange({ desde: startYMD, hasta: endYMD });
  };

  const handleReset = () => {
    setCompanyId("");
    setPreset("7d");
    setRange(deriveRange("7d"));
  };

  // El PDF lo genera microservice-pickups, pero el esquema se filtra igual: la URL llega
  // en una respuesta de la API y termina en un href y en el src de un iframe.
  const selectedPdfUrl = safeFileUrl(selected?.pdfUrl);

  const handleRetry = () => {
    setRange((r) => ({ ...r }));
  };

  // `error` holds the server body (object, `{ok:false, error}`) or the fixed
  // string api/holding.js returns on a network failure — only the body carries
  // text for the user, and apiBodyMessage reads its `error` field.
  const errorMessage =
    error && typeof error === "object" ? apiBodyMessage(error) : null;

  return (
    <div className="w-full min-w-0 p-6 space-y-5">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">{t("Retiros realizados")}</h1>
          <p className="text-sm text-fg-muted mt-1">
            {t("Evidencia firmada de los paquetes entregados por tus empresas al equipo logístico.")}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <DateRangeFilter value={preset} onChange={handleRangeChange} />

          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="h-10 rounded-lg border border-border bg-bg px-3 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="">{t("Todas las empresas")}</option>
            {companies.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleReset}
            className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-border bg-bg text-fg-muted hover:bg-bg-muted transition-colors"
            aria-label={t("Restablecer filtros")}
            title={t("Restablecer filtros")}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="hidden md:block rounded-lg border border-border overflow-hidden">
        <Table>
          <THead>
            <TR>
              <TH>{t("N°")}</TH>
              <TH>{t("Cliente")}</TH>
              <TH>{t("Fecha")}</TH>
              <TH>{t("Entregó")}</TH>
              <TH>{t("Recibió")}</TH>
              <TH>{t("Evidencia")}</TH>
            </TR>
          </THead>
          <TBody>
            {loading &&
              Array.from({ length: 5 }).map((_, i) => (
                <TR key={`sk-${i}`}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <TD key={j}>
                      <Skeleton className="h-4 w-full max-w-[140px]" />
                    </TD>
                  ))}
                </TR>
              ))}
            {!loading && pickups.length === 0 && (
              <TR>
                <TD colSpan={6} className="!p-0">
                  {error ? (
                    <EmptyState
                      icon={<FileX />}
                      title={t("No pudimos cargar los retiros")}
                      description={
                        errorMessage ??
                        t("Reintentá en unos segundos. Si persiste, contactá a soporte.")
                      }
                      action={
                        <Button variant="outline" onClick={handleRetry}>
                          {t("Reintentar")}
                        </Button>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={<PackageOpen />}
                      title={t("Sin retiros en el período seleccionado")}
                      description={t("Probá cambiar el rango de fechas o el filtro de cliente.")}
                    />
                  )}
                </TD>
              </TR>
            )}
            {!loading &&
              pickups.map((p) => {
                const receivedName = `${p.assignTo?.name ?? ""} ${p.assignTo?.lastName ?? ""}`.trim();
                const pickupAddress = getPickupAddress(p);
                return (
                  <TR key={p._id} className="cursor-pointer" onClick={() => setSelected(p)}>
                    <TD className="font-semibold tabular-nums">{p.correlative ?? "—"}</TD>
                    <TD>
                      <div className="font-medium text-fg">{p.client?.name || "—"}</div>
                      {pickupAddress && (
                        <div className="text-xs text-fg-muted">{pickupAddress}</div>
                      )}
                    </TD>
                    <TD>
                      <div>{p.date ? formatDate(p.date) : "—"}</div>
                      {p.date && <div className="text-xs text-fg-muted">{formatTime(p.date)}</div>}
                    </TD>
                    <TD>
                      {p.dispatcher ? (
                        <>
                          <div>{p.dispatcher.fullName || "—"}</div>
                          {p.dispatcher.rut && (
                            <div className="font-mono text-xs text-fg-muted">
                              {formatRut(p.dispatcher.rut)}
                            </div>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </TD>
                    <TD>
                      {p.assignTo ? (
                        <>
                          <div>{receivedName || "—"}</div>
                          {p.assignTo.email && (
                            <div className="text-xs text-fg-muted truncate max-w-[200px]">
                              {p.assignTo.email}
                            </div>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </TD>
                    <TD>
                      {p.pdfUrl ? (
                        <Badge tone="success" dot>
                          PDF
                        </Badge>
                      ) : (
                        <Badge tone="neutral">{t("Sin evidencia")}</Badge>
                      )}
                    </TD>
                  </TR>
                );
              })}
          </TBody>
        </Table>
      </div>

      <div className="md:hidden space-y-2">
        {loading &&
          Array.from({ length: 4 }).map((_, i) => (
            <div key={`m-sk-${i}`} className="rounded-lg border border-border bg-bg p-3 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        {!loading && pickups.length === 0 &&
          (error ? (
            <EmptyState
              icon={<FileX />}
              title={t("No pudimos cargar los retiros")}
              description={errorMessage ?? t("Reintentá en unos segundos.")}
              action={
                <Button variant="outline" onClick={handleRetry}>
                  {t("Reintentar")}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<PackageOpen />}
              title={t("Sin retiros en el período")}
              description={t("Probá cambiar el rango o el cliente.")}
            />
          ))}
        {!loading &&
          pickups.map((p) => {
            const receivedName = `${p.assignTo?.name ?? ""} ${p.assignTo?.lastName ?? ""}`.trim();
            const pickupAddress = getPickupAddress(p);
            return (
              <button
                key={p._id}
                type="button"
                onClick={() => setSelected(p)}
                className="w-full text-left rounded-lg border border-border bg-bg p-3 space-y-1 hover:bg-bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold tabular-nums">{t("N°")} {p.correlative ?? "—"}</span>
                  {p.pdfUrl ? (
                    <Badge tone="success" dot>
                      PDF
                    </Badge>
                  ) : (
                    <Badge tone="neutral">{t("Sin evidencia")}</Badge>
                  )}
                </div>
                <div className="font-medium text-fg">{p.client?.name || "—"}</div>
                {pickupAddress && (
                  <div className="text-xs text-fg-muted">{pickupAddress}</div>
                )}
                <div className="text-xs text-fg-muted">
                  {p.date ? `${formatDate(p.date)} · ${formatTime(p.date)}` : "—"}
                </div>
                <div className="text-xs text-fg-muted">
                  {t("Entregó")}: {p.dispatcher?.fullName || "—"} · {t("Recibió")}: {receivedName || "—"}
                </div>
              </button>
            );
          })}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={selected ? `${t("Retiro")} #${selected.correlative ?? "—"}` : ""}
        description={
          selected
            ? `${selected.date ? formatDateTime(selected.date) : "—"}${
                selected.client?.name ? ` · ${selected.client.name}` : ""
              }`
            : undefined
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setSelected(null)}>
              {t("Cerrar")}
            </Button>
            {selectedPdfUrl && (
              <a
                href={selectedPdfUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 active:bg-brand-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                {t("Abrir en pestaña")}
              </a>
            )}
          </>
        }
      >
        {selected && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <DetailBlock title={t("Cliente")}>
                <DetailLine label={t("Nombre")} value={selected.client?.name} />
                <DetailLine label={t("Dirección de retiro")} value={getPickupAddress(selected)} />
              </DetailBlock>
              <DetailBlock title={t("Entregados por")}>
                <DetailLine label={t("Nombre")} value={selected.dispatcher?.fullName} />
                <DetailLine
                  label="RUT"
                  value={selected.dispatcher?.rut ? formatRut(selected.dispatcher.rut) : ""}
                  mono
                />
              </DetailBlock>
              <DetailBlock title={t("Recibidos por")}>
                <DetailLine
                  label={t("Nombre")}
                  value={`${selected.assignTo?.name ?? ""} ${selected.assignTo?.lastName ?? ""}`.trim()}
                />
              </DetailBlock>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-fg-muted" />
                <h3 className="text-sm font-semibold text-fg">{t("Evidencia firmada")}</h3>
              </div>
              {selectedPdfUrl ? (
                <iframe
                  src={selectedPdfUrl}
                  title={`${t("Evidencia retiro")} #${selected.correlative}`}
                  className="w-full h-[60vh] rounded-lg border border-border bg-bg-muted"
                />
              ) : (
                <EmptyState
                  icon={<FileX />}
                  title={t("No hay PDF de evidencia")}
                  description={t("Este retiro no tiene archivo de evidencia firmado asociado.")}
                />
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

function DetailBlock({ title, children }) {
  return (
    <div className="rounded-lg border border-border bg-bg-muted/40 p-3 space-y-1">
      <div className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function DetailLine({ label, value, mono }) {
  return (
    <div className="text-sm">
      <span className="text-fg-muted">{label}: </span>
      <span className={cn("text-fg", mono && "font-mono")}>{value || "—"}</span>
    </div>
  );
}
